import React, { useId, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';

export interface Person {
  account_id: string;
  display_name: string | null;
  email_address?: string | null;
  active?: boolean;
  account_type?: string;
}

interface Props {
  /** What is being chosen: "Owner", "Involved". */
  label: string;
  /** Every Jira person; the picker offers real, active people (no app accounts). */
  people: Person[];
  /** The chosen Jira account ids. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Only one may be chosen: choosing another replaces the first. */
  single?: boolean;
  /** People that cannot be chosen here, such as the owner in the involved list. */
  exclude?: string[];
  /** Names for people that are chosen but not in `people`, by account id. */
  knownNames?: Record<string, string | null>;
  /** Said under the label when nobody is chosen. */
  emptyText: string;
}

const SHOWN = 8;

const INPUT =
  'w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-500 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100';

// Chosen people as chips you can remove, and a search that lists matching people to add. Every part is a
// plain button, so it works from the keyboard; Escape clears the search.
export default function PeoplePicker({ label, people, value, onChange, single = false, exclude = [], knownNames = {}, emptyText }: Props) {
  const inputId = useId();
  const [query, setQuery] = useState('');

  const nameOf = (id: string): string => people.find((p) => p.account_id === id)?.display_name ?? knownNames[id] ?? id;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return people
      .filter((p) => (p.account_type ?? 'atlassian') === 'atlassian' && p.active !== false)
      .filter((p) => !value.includes(p.account_id) && !exclude.includes(p.account_id))
      .filter((p) => (p.display_name ?? '').toLowerCase().includes(q) || (p.email_address ?? '').toLowerCase().includes(q))
      .sort((a, b) => (a.display_name ?? '').localeCompare(b.display_name ?? ''))
      .slice(0, SHOWN);
  }, [people, query, value, exclude]);

  const choose = (id: string) => {
    onChange(single ? [id] : [...value, id]);
    setQuery('');
  };

  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-900">
        {label}
      </label>

      {value.length === 0 ? (
        <p className="mt-1 text-sm text-gray-600">{emptyText}</p>
      ) : (
        <ul className="mt-2 flex flex-wrap gap-2">
          {value.map((id) => (
            <li key={id} className="inline-flex items-center gap-1 rounded-full bg-blue-50 py-1 pl-3 pr-1 text-sm text-blue-900 ring-1 ring-inset ring-blue-200">
              {nameOf(id)}
              <button
                type="button"
                onClick={() => onChange(value.filter((v) => v !== id))}
                aria-label={`Remove ${nameOf(id)}`}
                className="hit-area-sm rounded-full p-1 text-blue-700 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative mt-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden />
        <input
          id={inputId}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && query && (e.stopPropagation(), setQuery(''))}
          placeholder="Search by name or email"
          autoComplete="off"
          className={INPUT}
        />
      </div>

      {query.trim() && (
        <ul className="mt-1 divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white" aria-label={`People matching ${query.trim()}`}>
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-600">Nobody matches.</li>
          ) : (
            matches.map((p) => (
              <li key={p.account_id}>
                <button
                  type="button"
                  onClick={() => choose(p.account_id)}
                  className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus-visible:bg-blue-50"
                >
                  <span className="font-medium text-gray-900">
                    {p.display_name ?? p.account_id}
                    <span className="sr-only">: add to {label.toLowerCase()}</span>
                  </span>
                  {p.email_address && <span className="truncate text-xs text-gray-600">{p.email_address}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
